/* ---- Theme ---- */
/* ---- Image reveal ---- */
(() => {
  'use strict';
  const SEL = '.media > img, img[loading="lazy"]';
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const DONE_MS = 380;
  const mark = (img) => {
    const box = img.parentElement;
    if (box?.classList.contains('loading')) {
      if (REDUCED) box.classList.remove('loading');
      else { box.classList.add('is-complete'); setTimeout(() => box.classList.remove('loading', 'is-complete'), DONE_MS); }
    }
    if (REDUCED) img.classList.add('is-loaded');
    else requestAnimationFrame(() => requestAnimationFrame(() => img.classList.add('is-loaded')));
  };
  const near = new IntersectionObserver((entries) => entries.forEach((e) => {
    if (!e.isIntersecting) return;
    near.unobserve(e.target);
    if (!e.target.classList.contains('is-loaded')) e.target.parentElement?.classList.add('loading');
  }), { rootMargin: '200px 0px' });
  function watch(img) {
    if (img.dataset.ftWatched) return; img.dataset.ftWatched = '1';
    if (img.classList.contains('is-loaded')) return;
    if (img.getAttribute('fetchpriority') === 'high') { img.classList.add('is-loaded'); return; }
    const ready = img.complete && (img.naturalWidth > 0 || !img.currentSrc);
    if (ready) { mark(img); return; }
    if (img.parentElement) near.observe(img);
    img.addEventListener('load', () => { near.unobserve(img); mark(img); }, { once: true });
    img.addEventListener('error', () => mark(img), { once: true });
    if (img.complete && img.decode) img.decode().then(() => mark(img)).catch(() => {});
  }
  const sweep = (root = document) => { if (root.nodeType !== 1 && root !== document) return; if (root.matches?.(SEL)) watch(root); root.querySelectorAll?.(SEL).forEach(watch); };
  window.initLazyImages = sweep;
  const start = () => { sweep(); new MutationObserver((ms) => ms.forEach((m) => m.addedNodes.forEach((n) => n.nodeType === 1 && sweep(n)))).observe(document.body, { childList: true, subtree: true }); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
  window.addEventListener('load', () => sweep(), { once: true });
  window.addEventListener('pageshow', (e) => { if (e.persisted) document.querySelectorAll(SEL).forEach((i) => i.classList.add('is-loaded')); });
  if (window.Shopify?.designMode) document.addEventListener('shopify:section:load', (e) => sweep(e.target));
})();

/* ---- Off-screen animation pause ---- */
(() => {
  'use strict';
  if (window.Shopify?.designMode || !('IntersectionObserver' in window)) return;
  const io = new IntersectionObserver((entries) => entries.forEach((e) => {
    e.target.toggleAttribute('data-offscreen', !e.isIntersecting);
  }), { rootMargin: '100px 0px' });
  const start = () => document.querySelectorAll('.shopify-section').forEach((s) => io.observe(s));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
})();

/* ---- Faith FT ---- */
(() => {
  'use strict';
  const defineQueue = [];
  const nextTask = () => (window.scheduler?.yield ? window.scheduler.yield() : new Promise((r) => setTimeout(r, 0)));
  let pumping = false;
  const pumpDefines = async () => {
    pumping = true;
    while (defineQueue.length) {
      const [name, cls] = defineQueue.shift();
      try { if (!customElements.get(name)) customElements.define(name, cls); } catch (e) { console.warn('[ft]', name, e); }
      await nextTask();
    }
    pumping = false;
  };
  const safeDefine = (name, cls) => { defineQueue.push([name, cls]); if (!pumping) pumpDefines(); };

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const M = window.Motion;
  const EASE = [0.16, 1, 0.3, 1];
  const FLAG = (name) => document.body.dataset[name] !== 'false';
  // The editor's section/block picker previews never scroll, so nothing may wait to reveal there.
  const PREVIEW = !!window.Shopify?.visualPreviewMode;
  if (PREVIEW) document.documentElement.classList.remove('ft-anim');
  const REVEAL = FLAG('reveal') && !PREVIEW, TEXT_REVEAL = FLAG('textReveal') && !PREVIEW, IMAGE_REVEAL = FLAG('imageReveal') && !PREVIEW;
  const DESIGN = !!window.Shopify?.designMode;
  // Video play buttons: "play" keeps the button's own server-rendered (translated) label; "pause" comes from the locale.
  const labelPlayButton = (btn, playing) => {
    if (!btn.dataset.playLabel) btn.dataset.playLabel = btn.getAttribute('aria-label') || window.theme?.strings?.playVideo || '';
    const label = playing ? window.theme?.strings?.pauseVideo : btn.dataset.playLabel;
    if (label) btn.setAttribute('aria-label', label);
  };
  const FIRST_INPUT = DESIGN ? Promise.resolve() : new Promise((resolve) => {
    const events = ['pointerdown', 'touchstart', 'keydown', 'wheel'];
    const done = () => {
      events.forEach((e) => window.removeEventListener(e, done, true));
      window.removeEventListener('scroll', done);
      resolve();
    };
    events.forEach((e) => window.addEventListener(e, done, { capture: true, passive: true }));
    window.addEventListener('scroll', done, { passive: true });
  });
  if (M) window.FT_READY = true;
  else document.documentElement.classList.remove('ft-anim');

  /* ---- Near-viewport init ---- */
  // Heavy components (carousels, staggers, word splits, marquees…) set themselves up only once their section comes
  // within NEAR of the viewport, so below-the-fold work and its forced layouts stay off the critical load path.
  // Sections (always display:block) are the observed unit; later registrations for a section already reached run at once.
  const NEAR = '400px 0px';
  const nearPending = new Map();
  const nearDone = new WeakSet();
  let nearIO = null;
  const runNear = (target) => {
    const list = nearPending.get(target);
    nearPending.delete(target);
    nearDone.add(target);
    list?.forEach((fn) => { try { fn(); } catch (e) { console.warn('[ft]', e); } });
  };
  // Below the first section nothing is set up while the page is still loading (its CSS may still be arriving and the
  // main thread belongs to the first paint): observation starts at the load event, or sooner if the shopper scrolls.
  const nearStart = DESIGN || document.readyState === 'complete' ? Promise.resolve() : Promise.race([
    new Promise((resolve) => window.addEventListener('load', resolve, { once: true })),
    FIRST_INPUT,
  ]);
  let firstSection;
  const whenNear = (el, fn) => {
    if (DESIGN || !('IntersectionObserver' in window)) { fn(); return; }
    const target = el.closest('.shopify-section') || el;
    // The header and the template's first section keep their at-load timing (above the fold, LCP).
    firstSection ??= document.querySelector('#MainContent > .shopify-section');
    if (target === firstSection || target.classList.contains('shopify-section-group-header-group')) nearDone.add(target);
    if (nearDone.has(target)) { fn(); return; }
    const list = nearPending.get(target);
    if (list) { list.push(fn); return; }
    nearPending.set(target, [fn]);
    nearStart.then(() => {
      nearIO ||= new IntersectionObserver((entries) => entries.forEach((e) => {
        if (!e.isIntersecting) return;
        nearIO.unobserve(e.target);
        runNear(e.target);
      }), { rootMargin: NEAR });
      nearIO.observe(target);
    });
  };
  window.theme = Object.assign(window.theme || {}, { whenNear });

  /* ---- Smooth scroll ---- */
  const startLenis = () => {
    if (!window.Lenis || window.lenis) return;
    const lenis = new Lenis({ lerp: 0.2, wheelMultiplier: 1 });
    window.lenis = lenis;
    const raf = (t) => { lenis.raf(t); requestAnimationFrame(raf); };
    requestAnimationFrame(raf);
    const adopt = () => { if (Math.abs(window.scrollY - (lenis.animatedScroll ?? 0)) > 1) lenis.scrollTo(window.scrollY, { immediate: true, force: true }); };
    requestAnimationFrame(adopt);
    window.addEventListener('load', adopt, { once: true });
    window.addEventListener('pageshow', (e) => { if (e.persisted) adopt(); });
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a[href^="#"]'); if (!a || a.getAttribute('href') === '#') return;
      const el = document.querySelector(a.getAttribute('href')); if (!el) return;
      e.preventDefault(); lenis.scrollTo(el, { offset: -(document.querySelector('.header-wrapper')?.offsetHeight || 0) });
    });
  };
  // Lenis starts on the visitor's first input, a mouse move included, so on a real desktop it is running before the
  // first scroll, while its set-up and per-frame loop stay off the page load (they showed up as blocking time).
  if (!REDUCED && !DESIGN && !window.matchMedia('(pointer: coarse)').matches && document.body.dataset.smoothScroll !== 'false') {
    const wake = new Promise((resolve) => {
      FIRST_INPUT.then(resolve);
      window.addEventListener('pointermove', resolve, { once: true, passive: true });
    });
    wake.then(() => {
      if (window.Lenis) startLenis();
      else document.querySelector('script[data-lenis]')?.addEventListener('load', startLenis, { once: true });
    });
  }

  const settling = new Set();
  let settleQueued = false;
  const settleCheck = () => {
    settleQueued = false;
    if (!settling.size) return;
    if (window.scrollY + window.innerHeight < document.documentElement.scrollHeight - 2) return;
    settling.forEach((entry) => {
      if (!entry.el.isConnected) { settling.delete(entry); return; }
      const r = entry.el.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) { settling.delete(entry); entry.play(); }
    });
  };
  // At most one layout read per frame while scrolling, and none at all once everything has played.
  const queueSettle = () => { if (settling.size && !settleQueued) { settleQueued = true; requestAnimationFrame(settleCheck); } };
  window.addEventListener('scroll', queueSettle, { passive: true });
  window.addEventListener('resize', queueSettle);
  window.addEventListener('load', () => requestAnimationFrame(settleCheck), { once: true });

  function whenSettled(el, play) {
    settling.add({ el, play });
    if (document.readyState === 'complete') requestAnimationFrame(settleCheck);
  }

  /* ---- Wait for pictures, at most 600ms, without reading layout ---- */
  function mediaLoaded(root) {
    const images = Array.from(root.querySelectorAll('img')).filter((img) => !img.complete);
    if (!images.length) return Promise.resolve();
    return Promise.race([
      Promise.all(images.map((img) => new Promise((resolve) => {
        img.addEventListener('load', resolve, { once: true });
        img.addEventListener('error', resolve, { once: true });
      }))),
      new Promise((resolve) => setTimeout(resolve, 600)),
    ]);
  }

  /* ---- <ft-reveal> ---- */
  class FtReveal extends HTMLElement {
    get type()  { return this.getAttribute('data-ft') || 'rise'; }
    get delay() { return (parseInt(this.getAttribute('data-ft-delay')) || 0) / 1000; }
    get target() {
      return this.type === 'zoom' ? (this.querySelector('.media > img, .media > video') || this) : this;
    }

    connectedCallback() {
      if (REDUCED || !M || !REVEAL || this.closest('.ftc__slide-clone')) { this.setAttribute('played', ''); return; }
      if (this.hasAttribute('played')) return;
      // The hidden start state comes from CSS (html.ft-anim ft-reveal:not([played])); it is copied inline only
      // when the reveal plays, instead of writing styles on every element at load.
      FtReveal.observer.observe(this.watched);
      whenSettled(this, () => this.play());
    }

    // A masked word is fully clipped by its .ft-word until it plays, so the observer watches the mask instead.
    get watched() {
      const mask = this.type === 'mask' && this.parentElement?.classList.contains('ft-word') ? this.parentElement : null;
      if (mask) mask.ftReveal = this;
      return mask || this;
    }

    static get observer() {
      return FtReveal._io ||= new IntersectionObserver((entries) => entries.forEach((e) => {
        if (!e.isIntersecting) return;
        FtReveal._io.unobserve(e.target);
        (e.target.ftReveal || e.target).play();
      }), { rootMargin: '0px 0px -10% 0px' });
    }

    setStart() {
      const s = this.target.style;
      switch (this.type) {
        case 'fade':       s.opacity = '0'; break;
        case 'rise':       s.opacity = '0'; s.transform = 'translateY(min(2rem, 90%))'; break;
        case 'rise-large': s.opacity = '0'; s.transform = 'translateY(90%)'; break;
        case 'mask':       s.transform = 'translateY(130%)'; break;
        case 'zoom':       s.transform = 'scale(1.3)'; break;
      }
    }

    async play() {
      if (this.hasAttribute('played')) return;
      this.setStart(); // hold the CSS start state inline before [played] lifts the CSS rule
      this.setAttribute('played', '');
      FtReveal._io?.unobserve(this.watched);
      await mediaLoaded(this);
      const t = this.target;
      const opts = { duration: 1.5, delay: this.delay, easing: EASE };
      switch (this.type) {
        case 'fade':       await M.animate(t, { opacity: 1 }, opts).finished; break;
        case 'rise':       await M.animate(t, { opacity: 1, transform: 'translateY(0)' }, opts).finished; break;
        case 'rise-large': await M.animate(t, { opacity: 1, transform: 'translateY(0)' }, { ...opts, duration: 1 }).finished; break;
        case 'mask':       await M.animate(t, { transform: 'translateY(0)' }, { ...opts, duration: 1 }).finished; break;
        case 'zoom':       await M.animate(t, { transform: 'scale(1)' }, { ...opts, duration: 1.3 }).finished; break;
      }
      this.classList.add('ft-done');
      t.style.transform = ''; t.style.opacity = '';
    }
  }
  safeDefine('ft-reveal', FtReveal);

  /* ---- <ft-words> ---- */
  class FtWords extends HTMLElement {
    connectedCallback() {
      if (this.hasAttribute('ready') || this.queued) return;
      if (!TEXT_REVEAL || REDUCED || !M || !REVEAL) { this.setAttribute('ready', ''); return; }
      // Inside a marquee the split must happen before the scroller measures and clones its list, as before.
      if (this.closest('.scroller')) { this.split(); return; }
      this.queued = true;
      whenNear(this, () => { this.queued = false; if (this.isConnected && !this.hasAttribute('ready')) this.split(); });
    }

    split() {
      if (this.dataset.split === 'lines') { this.splitLines(); return; }
      const type = this.getAttribute('data-ft') || 'rise-large';
      let index = 0;

      const split = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          if (!node.nodeValue.trim()) return;
          const frag = document.createDocumentFragment();
          node.nodeValue.split(/(\s+)/).forEach((part) => {
            if (!part.trim()) { frag.appendChild(document.createTextNode(part)); return; }
            const word = document.createElement('span');
            word.className = 'ft-word';
            const inner = document.createElement('ft-reveal');
            // Each word is clipped by its .ft-word mask, so the default rise needs no fade: 'mask' slides it up from
            // fully below the mask at full opacity (never semi-transparent text, which fails contrast checks mid-way).
            inner.setAttribute('data-ft', type === 'rise-large' ? 'mask' : type);
            inner.setAttribute('data-ft-delay', index++ * 30);
            inner.textContent = part;
            word.appendChild(inner);
            frag.appendChild(word);
          });
          node.replaceWith(frag);
          return;
        }
        if (node.nodeType === Node.ELEMENT_NODE && node.tagName !== 'BR') {
          Array.from(node.childNodes).forEach(split);
        }
      };

      Array.from(this.childNodes).forEach(split);
      this.setAttribute('ready', '');
    }

    splitLines() {
      const LINE = 0.12;
      const WORD = 0.04;
      const masks = [];

      const split = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          if (!node.nodeValue.trim()) return;
          const frag = document.createDocumentFragment();
          node.nodeValue.split(/(\s+)/).forEach((part) => {
            if (!part.trim()) { frag.appendChild(document.createTextNode(part)); return; }
            const mask = document.createElement('span');
            mask.className = 'ft-word';
            const word = document.createElement('span');
            word.className = 'ft-word__inner';
            word.style.transform = 'translateY(130%)'; // hidden by the .ft-word mask; no fade, see 'mask' above
            word.textContent = part;
            mask.appendChild(word);
            frag.appendChild(mask);
            masks.push(mask);
          });
          node.replaceWith(frag);
          return;
        }
        if (node.nodeType === Node.ELEMENT_NODE && node.tagName !== 'BR') {
          Array.from(node.childNodes).forEach(split);
        }
      };
      Array.from(this.childNodes).forEach(split);
      this.setAttribute('ready', '');

      let next = 0;
      let delay = (parseInt(this.dataset.ftDelay) || 0) / 1000;
      const pending = new Set();
      const queuedOnce = new Set();
      let queued = false;
      this.masks = masks;

      const flush = () => {
        queued = false;
        const now = performance.now() / 1000;
        const batch = masks.filter((m) => pending.has(m));
        pending.clear();
        const lines = [];
        let top = null;
        batch.forEach((m) => {
          const t = m.offsetTop;
          if (top === null || Math.abs(t - top) > 4) { lines.push([]); top = t; }
          lines[lines.length - 1].push(m.firstElementChild);
        });
        lines.forEach((words) => {
          const start = Math.max(now + delay, next);
          delay = 0;
          next = start + LINE;
          M.animate(words, { transform: ['translateY(130%)', 'translateY(0)'] },
            { duration: 0.9, easing: EASE, delay: M.stagger(WORD, { start: start - now }) })
            .finished.then(() => words.forEach((w) => { w.style.transform = ''; }))
            .catch(() => {});
        });
      };
      const queue = (mask) => {
        if (queuedOnce.has(mask)) return;
        queuedOnce.add(mask);
        pending.add(mask);
        if (!queued) { queued = true; requestAnimationFrame(flush); }
      };

      this.io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          this.io.unobserve(e.target);
          queue(e.target);
        });
      }, { rootMargin: '0px 0px -10% 0px' });
      masks.forEach((m) => this.io.observe(m));
      whenSettled(this, () => masks.forEach((m) => {
        this.io.unobserve(m);
        queue(m);
      }));
    }

    disconnectedCallback() {
      if (!this.io) return;
      this.io.disconnect();
      this.masks.forEach((m) => { m.firstElementChild.style.opacity = ''; m.firstElementChild.style.transform = ''; });
    }
  }
  safeDefine('ft-words', FtWords);

  /* ---- <custom-select> ---- */
  class CustomSelect extends HTMLElement {
    connectedCallback() {
      this.btn = this.querySelector('.custom-select__btn');
      this.listbox = this.querySelector('.custom-select__listbox');
      if (!this.btn || !this.listbox || this.bound) return;
      this.bound = true;

      this.value = this.querySelector('.custom-select__value');
      this.media = this.querySelector('.custom-select__media');
      this.native = this.querySelector('.custom-select__native');
      if (this.native) { this.native.tabIndex = -1; this.native.setAttribute('aria-hidden', 'true'); }
      this.options = Array.from(this.querySelectorAll('.custom-select__option'));
      this.selected = this.querySelector('[aria-selected="true"]') || this.options[0];
      if (this.native && this.native.value !== (this.selected?.dataset.value ?? '')) this.setValue(this.native.value);

      this.btn.addEventListener('click', () => (this.isOpen ? this.hide() : this.show()));
      this.addEventListener('keydown', (e) => this.onKey(e));
      this.listbox.addEventListener('click', (e) => {
        const li = e.target.closest('.custom-select__option');
        if (li && li.getAttribute('aria-disabled') !== 'true') this.select(li);
      });
      this.listbox.addEventListener('mousemove', (e) => {
        const li = e.target.closest('.custom-select__option');
        if (li) this.focusOption(li);
      });
      this.away = (e) => { if (!this.contains(e.target)) this.hide(); };

      this.classList.add('is-ready');
    }

    disconnectedCallback() { document.removeEventListener('pointerdown', this.away, true); clearTimeout(this.typeTimer); }

    get enabled() { return this.options.filter((o) => o.getAttribute('aria-disabled') !== 'true' && o.getClientRects().length); }

    show() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.classList.add('is-open');
      this.btn.setAttribute('aria-expanded', 'true');
      this.listbox.setAttribute('aria-hidden', 'false');
      // pointerdown, not click: iOS Safari fires no click for a tap on a non-clickable area, so the list would stay open.
      document.addEventListener('pointerdown', this.away, true);
      this.focusOption(this.selected);
      this.listbox.focus();
    }

    hide() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.classList.remove('is-open');
      this.btn.setAttribute('aria-expanded', 'false');
      this.listbox.setAttribute('aria-hidden', 'true');
      this.focused?.classList.remove('is-focused');
      this.focused = null;
      document.removeEventListener('pointerdown', this.away, true);
    }

    focusOption(li) {
      if (!li) return;
      this.focused?.classList.remove('is-focused');
      this.focused = li;
      li.classList.add('is-focused');
      this.listbox.setAttribute('aria-activedescendant', li.id);
      const top = li.offsetTop, bottom = top + li.offsetHeight;
      if (bottom > this.listbox.scrollTop + this.listbox.clientHeight) this.listbox.scrollTop = bottom - this.listbox.clientHeight;
      else if (top < this.listbox.scrollTop) this.listbox.scrollTop = top;
    }

    select(li) {
      if (li !== this.selected) {
        this.selected?.setAttribute('aria-selected', 'false');
        li.setAttribute('aria-selected', 'true');
        this.selected = li;
        this.value.textContent = li.dataset.label || li.querySelector('.custom-select__option-text')?.textContent.trim() || li.textContent.trim();
        const art = li.querySelector('.custom-select__option-media');
        if (this.media && art) this.media.innerHTML = art.innerHTML;
        this.listbox.setAttribute('aria-activedescendant', li.id);
        if (this.native) {
          this.native.value = li.dataset.value;
          this.native.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          this.dispatchEvent(new CustomEvent('change', { bubbles: true, detail: { value: li.dataset.value } }));
        }
      }
      this.hide();
      this.btn.focus();
    }

    setValue(value, { silent = true } = {}) {
      const li = this.options?.find((o) => o.dataset.value === String(value));
      if (!li || li === this.selected) return;
      this.selected?.setAttribute('aria-selected', 'false');
      li.setAttribute('aria-selected', 'true');
      this.selected = li;
      this.value.textContent = li.dataset.label || li.querySelector('.custom-select__option-text')?.textContent.trim() || li.textContent.trim();
      this.listbox.setAttribute('aria-activedescendant', li.id);
      if (this.native) {
        this.native.value = li.dataset.value;
        if (!silent) this.native.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (!silent) {
        this.dispatchEvent(new CustomEvent('change', { bubbles: true, detail: { value: li.dataset.value } }));
      }
    }

    onKey(e) {
      if (!this.isOpen) {
        if (['ArrowUp', 'ArrowDown', 'Enter', ' '].includes(e.key)) { e.preventDefault(); this.show(); }
        return;
      }
      const list = this.enabled;
      const at = list.indexOf(this.focused);
      switch (e.key) {
        case 'ArrowDown': e.preventDefault(); this.focusOption(list[Math.min(at + 1, list.length - 1)]); break;
        case 'ArrowUp': e.preventDefault(); this.focusOption(list[Math.max(at - 1, 0)]); break;
        case 'Home': e.preventDefault(); this.focusOption(list[0]); break;
        case 'End': e.preventDefault(); this.focusOption(list[list.length - 1]); break;
        case 'Enter': case ' ': e.preventDefault(); if (this.focused) this.select(this.focused); break;
        case 'Escape': e.preventDefault(); this.hide(); this.btn.focus(); break;
        case 'Tab': this.hide(); break;
        default: {
          if (e.key.length !== 1) return;
          this.typed = (this.typed || '') + e.key.toLowerCase();
          clearTimeout(this.typeTimer);
          this.typeTimer = setTimeout(() => { this.typed = ''; }, 500);
          const hit = list.find((o) => o.textContent.trim().toLowerCase().startsWith(this.typed));
          if (hit) this.focusOption(hit);
        }
      }
    }
  }
  safeDefine('custom-select', CustomSelect);

  /* ---- <ft-wordmark> ---- */
  class FtWordmark extends HTMLElement {
    connectedCallback() {
      if (this.queued || this.resize) return;
      this.queued = true;
      // Measures text and fonts: wait until the footer is close.
      whenNear(this, () => { this.queued = false; if (this.isConnected) this.init(); });
    }

    init() {
      this.glyphs = this.querySelectorAll('.footer-wordmark__glyph');
      if (!this.glyphs.length) return;

      const slices = this.hasAttribute('data-ratio');
      const sync = () => { this.fit(); if (slices) this.measure(); };
      document.fonts?.ready.then(() => { this.fitted = 0; sync(); });
      this.resize = new ResizeObserver(() => {
        if (this.clientWidth === this.fitted) return;
        this.fitted = this.clientWidth;
        sync();
      });
      this.resize.observe(this);

      if (!this.hasAttribute('data-animate') || REDUCED || !REVEAL) {
        this.classList.add('is-in', 'is-revealed');
        return;
      }

      const step = Number(this.dataset.stagger) || 60;
      const start = () => {
        if (this.started) return;
        this.started = true;
        this.classList.add('is-in');
        this.timer = setTimeout(() => this.classList.add('is-revealed'), (this.glyphs.length - 1) * step + 700);
      };

      if (M) M.inView(this, start, { margin: '0px 0px -10% 0px' });
      else new IntersectionObserver((entries, obs) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        obs.disconnect(); start();
      }, { rootMargin: '0px 0px -10% 0px' }).observe(this);
      whenSettled(this, start);
    }

    disconnectedCallback() { clearTimeout(this.timer); this.resize?.disconnect(); this.resize = null; }

    fit() {
      const text = this.querySelector('.footer-wordmark__text');
      const letters = this.querySelectorAll('.footer-wordmark__letter');
      if (!text || !letters.length) return;
      const avail = this.clientWidth;
      if (!avail) return;
      const first = letters[0].getBoundingClientRect();
      const last = letters[letters.length - 1].getBoundingClientRect();
      if (Math.abs(last.top - first.top) > 1) return;
      const gap = parseFloat(getComputedStyle(text).letterSpacing) || 0;
      const width = last.right - first.left - gap;
      const base = parseFloat(getComputedStyle(text).fontSize);
      if (width <= 0 || !base) return;
      this.style.setProperty('--footer-wordmark-fitted', `${(base * avail) / width}px`);
    }

    measure() {
      const text = this.querySelector('.footer-wordmark__text');
      if (!text) return;
      const w = text.offsetWidth, h = text.offsetHeight, ratio = Number(this.dataset.ratio) || 1;
      if (!w || !h) return;
      const bw = Math.max(w, h * ratio), bh = bw / ratio;
      const ox = (bw - w) / 2, oy = (bh - h) / 2;
      const at = [...this.glyphs].map((glyph) => [glyph.offsetLeft, glyph.offsetTop]);
      this.style.setProperty('--wordmark-bg-width', `${bw}px`);
      this.style.setProperty('--wordmark-bg-height', `${bh}px`);
      this.glyphs.forEach((glyph, i) => {
        glyph.style.setProperty('--letter-bg-x', `${-(at[i][0] + ox)}px`);
        glyph.style.setProperty('--letter-bg-y', `${-(at[i][1] + oy)}px`);
      });
      this.classList.add('is-measured');
    }
  }
  safeDefine('ft-wordmark', FtWordmark);

  /* ---- <ft-video> ---- */
  class FtVideo extends HTMLElement {
    get video() { return this.querySelector('video'); }
    play()  { const v = this.video; if (v) { v.muted = !this.hasAttribute('unmuted'); Promise.resolve(v.play()).then(() => this.setAttribute('playing', '')).catch(() => {}); } }
    pause() { const v = this.video; if (v && !v.paused) v.pause(); this.removeAttribute('playing'); }
    connectedCallback() {
      if (this.bound) return;
      const video = this.video; if (!video) return;
      this.bound = true;
      this.classList.add('loading');
      const ready = () => this.classList.remove('loading');
      if (video.readyState >= 1) ready(); else video.addEventListener('loadedmetadata', ready, { once: true });
      const playBtn = this.querySelector('.ft-play');
      // The label follows the video itself, since play() only starts once the browser allows it.
      video.addEventListener('play',  () => { this.setAttribute('playing', ''); if (playBtn) labelPlayButton(playBtn, true); });
      video.addEventListener('pause', () => { this.removeAttribute('playing'); if (playBtn) labelPlayButton(playBtn, false); });
      video.addEventListener('ended', () => { this.removeAttribute('playing'); if (playBtn) labelPlayButton(playBtn, false); });

      playBtn?.addEventListener('click', (e) => { e.stopPropagation(); this.hasAttribute('playing') ? this.pause() : this.play(); });
      if (playBtn) this.addEventListener('click', (e) => {
        if (e.target.closest('a, button, input, select, textarea, label')) return;
        playBtn.click();
      });

      const muteBtn = this.querySelector('.ft-mute');
      muteBtn?.addEventListener('click', (e) => { e.stopPropagation(); video.muted = !video.muted; this.toggleAttribute('unmuted', !video.muted); muteBtn.setAttribute('aria-label', video.muted ? 'Unmute' : 'Mute'); });

      if (this.hasAttribute('autoplay') && M) {
        M.inView(this, () => { this.play(); return () => this.pause(); }, { margin: '0px 0px -10% 0px' });
      }
    }
  }
  safeDefine('ft-video', FtVideo);

  /* ---- <ft-parallax> ---- */
  class FtParallax extends HTMLElement {
    connectedCallback() {
      if (REDUCED || !M || this.queued || this.stop) return;
      this.queued = true;
      whenNear(this, () => { this.queued = false; if (this.isConnected) this.init(); });
    }
    init() {
      const img = this.querySelector('img, video, svg');
      if (!img) return;
      const speed = parseFloat(this.getAttribute('data-speed')) || 0.25;
      const travel = speed * 100;
      img.style.willChange = 'transform';
      this.stop = M.scroll(({ y }) => {
        const p = y.progress;
        img.style.transform = `translateY(${(-travel / 2) + travel * p}%)`;
      }, { target: this.parentElement, offset: ['start end', 'end start'] });
    }
    disconnectedCallback() { this.stop?.(); this.stop = null; }
  }
  safeDefine('ft-parallax', FtParallax);

  /* ---- <ft-list> ---- */
  class FtList extends HTMLElement {
    get cards() { return Array.from(this.querySelectorAll('.card')); }

    connectedCallback() {
      if (REDUCED || !M) return;
      const distance = window.matchMedia('(max-width: 749px)').matches ? 30 : 50;
      M.animate(this.cards, { opacity: 0, transform: `translateY(${distance}px)` }, { duration: 0 });

      M.inView(this, () => {
        const vh = window.innerHeight;
        const visible = [], hidden = [];
        this.cards.forEach((card) => {
          const r = card.getBoundingClientRect();
          (r.top < vh && r.bottom > 0 ? visible : hidden).push(card);
        });

        mediaLoaded(this).then(() => {
          M.animate(visible, { opacity: 1, transform: 'translateY(0)' },
            { duration: 0.6, easing: EASE, delay: M.stagger(0.08) });
        });

        hidden.forEach((card) => {
          M.inView(card, async () => {
            await mediaLoaded(card);
            M.animate(card, { opacity: 1, transform: 'translateY(0)' }, { duration: 0.6, easing: EASE });
          }, { margin: '0px 0px -10% 0px' });
        });
      });
    }
  }
  safeDefine('ft-list', FtList);

  /* ---- <ft-stagger> ---- */
  class FtStagger extends HTMLElement {
    get targets() {
      const selector = this.getAttribute('data-ft-targets') || ':scope > *';
      return Array.from(this.querySelectorAll(selector)).filter((el) => el.getClientRects().length > 0);
    }

    connectedCallback() {
      if (REDUCED || !M || !REVEAL || this.hasAttribute('played')) { this.setAttribute('data-armed', ''); return; }
      if (this.queued || this.hasAttribute('data-armed')) return;
      // Items stay hidden by CSS until armed; arming reads layout, so it waits until the section is close.
      this.queued = true;
      whenNear(this, () => { this.queued = false; if (this.isConnected) this.arm(); });
    }

    arm() {
      if (this.hasAttribute('played') || this.hasAttribute('data-armed')) { this.setAttribute('data-armed', ''); return; }
      const items = this.targets;
      if (!items.length) { this.setAttribute('data-armed', ''); return; }
      const distance = window.matchMedia('(max-width: 749px)').matches ? 20 : 32;
      items.forEach((el) => { el.style.opacity = '0'; el.style.transform = `translateY(${distance}px)`; });
      this.setAttribute('data-armed', '');

      const release = (list) => list.forEach((el) => { el.style.transform = ''; el.style.opacity = ''; });
      const rise = (list, stagger) => M.animate(list, { opacity: 1, transform: 'translateY(0)' }, {
        duration: 0.9, easing: EASE, delay: stagger ? M.stagger(0.07) : 0,
      }).finished.then(() => release(list));

      M.inView(this, () => {
        if (this.hasAttribute('played')) return;
        this.setAttribute('played', '');
        const vh = window.innerHeight;
        const visible = [], below = [];
        items.forEach((el) => {
          if (!el.isConnected) return;
          const r = el.getBoundingClientRect();
          (r.top < vh && r.bottom > 0 ? visible : below).push(el);
        });
        Promise.race([mediaLoaded(visible[0] || this), new Promise((r) => setTimeout(r, 400))])
          .then(() => rise(visible, true));
        below.forEach((el) => {
          let done = false;
          const play = () => { if (done) return; done = true; rise([el], false); };
          M.inView(el, play, { margin: '0px 0px -10% 0px' });
          whenSettled(el, play);
        });
      }, { margin: '0px 0px -5% 0px' });
    }
  }
  safeDefine('ft-stagger', FtStagger);

  // touch: also drive touch/pen drags from JS (the track sets touch-action: pan-y, so the page still scrolls vertically).
  // noDrag: a selector whose mouse presses are left to the browser (text the shopper can select); touch still swipes.
  function enableDragScroll(el, { axis = 'x', onEnd, onStart, settle = false, touch = false, noDrag = null } = {}) {
    let start = 0, startScroll = 0, moved = false, down = false, pid = null;
    let lastPos = 0, lastT = 0, velocity = 0;
    const prop = axis === 'x' ? 'scrollLeft' : 'scrollTop';
    el.addEventListener('dragstart', (e) => e.preventDefault());
    el.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse' ? e.button !== 0 : !touch || !e.isPrimary) return;
      if (noDrag && e.pointerType === 'mouse' && e.target.closest?.(noDrag)) return;
      down = true; moved = false; pid = e.pointerId;
      start = axis === 'x' ? e.clientX : e.clientY; startScroll = el[prop];
      lastPos = start; lastT = e.timeStamp; velocity = 0;
    });
    window.addEventListener('pointermove', (e) => {
      if (!down || e.pointerId !== pid) return;
      const p = axis === 'x' ? e.clientX : e.clientY;
      const d = p - start;
      if (!moved && Math.abs(d) > 6) {
        moved = true; onStart?.(); startScroll = el[prop] + d;
        el.classList.add('is-dragging'); try { el.setPointerCapture(pid); } catch {}
      }
      if (moved) { el[prop] = startScroll - d; e.preventDefault(); }
      const dt = e.timeStamp - lastT;
      if (dt > 0) { velocity = velocity * 0.4 + ((lastPos - p) / dt) * 0.6; lastPos = p; lastT = e.timeStamp; }
    });
    const end = (e) => {
      if (!down || e.pointerId !== pid) return;
      down = false;
      if (moved) {
        try { el.releasePointerCapture(pid); } catch {}
        const swallow = (ev) => { ev.preventDefault(); ev.stopPropagation(); };
        el.addEventListener('click', swallow, { capture: true, once: true });
        setTimeout(() => { el.removeEventListener('click', swallow, { capture: true }); moved = false; }, 0);
        if (e.timeStamp - lastT > 90) velocity = 0;
        if (!settle) { onEnd?.(el[prop], startScroll, velocity); setTimeout(() => el.classList.remove('is-dragging'), 0); return; }
        let done = false;
        const release = () => {
          if (done) return; done = true;
          el.removeEventListener('scrollend', release);
          clearTimeout(timer);
          el.classList.remove('is-dragging');
          el.dispatchEvent(new Event('scrollend'));
        };
        const timer = setTimeout(release, 900);
        el.addEventListener('scrollend', release);
        onEnd?.(el[prop], startScroll, velocity);
      }
    };
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
  }

  /* ---- <ft-carousel> ---- */
  class FtCarousel extends HTMLElement {
    static BP = { tablet: window.matchMedia('(max-width: 989px)'), mobile: window.matchMedia('(max-width: 749px)') };
    // Set up at once: the hero and header (in view at load, other scripts listen to their first change) and anything
    // inside a closed drawer or modal. Product galleries call ensure() themselves before reading carousel state.
    static EAGER = 'slideshow-component, ft-drawer, ft-modal, .shopify-section-group-header-group, [data-ft-eager]';

    connectedCallback() {
      if (this.ready || this.queued) return;
      this.index ??= 0;
      if (!this.querySelector('.ftc__track')) return;
      if (this.closest(FtCarousel.EAGER)) { this.init(); return; }
      // Everything else is laid out by CSS already; the measuring set-up (loop clones, per-view, controls) waits
      // until the section is near the viewport, or until another script calls go().
      this.queued = true;
      whenNear(this, () => { this.queued = false; this.ensure(); });
    }

    ensure() {
      if (!this.ready && this.isConnected && this.querySelector('.ftc__track')) this.init();
      return !!this.ready;
    }

    init() {
      this.ready = true;
      this.track  = this.querySelector('.ftc__track');

      const scope = this.controlsScope();
      const q = (sel) => this.querySelector(sel) || scope?.querySelector(sel) || null;
      this.prev   = q('[data-prev]');
      this.next   = q('[data-next]');
      const every = (sel) => [...new Set([...this.querySelectorAll(sel), ...(scope ? scope.querySelectorAll(sel) : [])])];
      this.prevs  = every('[data-prev]');
      this.nexts  = every('[data-next]');
      this.dots   = q('[data-dots]');
      this.bar    = q('[data-bar] > span');
      this.cur    = q('[data-current]');
      this.total  = q('[data-total]');
      this.slides = Array.from(this.track.children);
      this.loop   = this.hasAttribute('data-loop');
      this.delay  = parseInt(this.getAttribute('data-autoplay')) || 0;
      this.index  = 0;

      this.applyPerView();
      this.setupLoop();
      FtCarousel.BP.tablet.addEventListener('change', () => this.applyPerView());
      FtCarousel.BP.mobile.addEventListener('change', () => this.applyPerView());

      this.buildDots();
      if (this.total) this.total.textContent = String(this.slides.length).padStart(2, '0');

      this.prevs.forEach((b) => b.addEventListener('click', () => this.go(this.index - 1)));
      this.nexts.forEach((b) => b.addEventListener('click', () => this.go(this.index + 1)));
      this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });
      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') this.go(this.index + 1);
        if (e.key === 'ArrowLeft')  this.go(this.index - 1);
      });
      this.enableDrag();

      if (this.delay && !REDUCED) {
        new IntersectionObserver((en) => en.forEach((e) => e.isIntersecting ? this.play() : this.pause()), { threshold: 0.25 }).observe(this);
        if (this.hasAttribute('data-pause-hover')) {
          this.addEventListener('mouseenter', () => this.pause());
          this.addEventListener('mouseleave', () => this.play());
        }
        this.addEventListener('focusin',  () => this.pause());
        this.addEventListener('focusout', () => this.play());
      }
      if (DESIGN) {
        this.closest('.shopify-section')?.addEventListener('shopify:block:select', (e) => {
          let node = e.target;
          while (node && node.parentElement !== this.track) node = node.parentElement;
          const i = node ? this.slides.indexOf(node) : -1;
          if (i > -1) this.go(i);
        });
      }
      this.update();
    }

    refresh() {
      if (!this.ready || !this.track) return;
      this.slides = Array.from(this.track.children);
      this.real = this.slides.filter((s) => !s.classList.contains('ftc__slide-clone'));
      this.index = Math.max(0, Math.min(this.index, this.slides.length - 1));
      if (this.total) this.total.textContent = String(this.slides.length).padStart(2, '0');
      this.applyPerView();
    }

    controlsScope() {
      const sel = this.getAttribute('data-controls');
      const section = this.closest('section, .shopify-section') || document;
      if (sel) return section.querySelector(sel) || document.querySelector(sel);
      if (this.id) return section.querySelector(`.ftc__controls[data-for="${this.id}"]`);
      return null;
    }

    applyPerView() {
      if (!this.ready) return;
      const d = parseFloat(this.getAttribute('data-desktop')) || 3;
      const t = parseFloat(this.getAttribute('data-tablet'))  || Math.min(d, 2);
      const m = parseFloat(this.getAttribute('data-mobile'))  || 1.2;
      const per = FtCarousel.BP.mobile.matches ? m : FtCarousel.BP.tablet.matches ? t : d;
      this.track.style.setProperty('--ftc-per-view', per);
      this.perView = per;
      const locked = this.realSlides.length <= Math.floor(per);
      this.toggleAttribute('data-lock', locked);
      this.controlsScope()?.toggleAttribute('data-lock', locked);
      this.update();
    }

    quiet(c) {
      if (!this.hasAttribute('data-live-clones')) { c.setAttribute('inert', ''); return; }
      c.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((el) => el.setAttribute('tabindex', '-1'));
    }
    get realSlides() { return this.slides.filter((s) => !s.classList.contains('ftc__slide-clone')); }

    setupLoop() {
      if (!this.loop || this.cloned || this.hasAttribute('data-lock')) return;
      const n = this.slides.length, k = Math.max(1, Math.ceil(this.perView));
      if (n <= k) return;
      const first = this.slides.slice(0, k), last = this.slides.slice(-k);
      last.forEach((s) => { const c = s.cloneNode(true); c.classList.add('ftc__slide-clone'); c.setAttribute('aria-hidden', 'true'); this.quiet(c); this.track.insertBefore(c, this.track.firstChild); });
      first.forEach((s) => { const c = s.cloneNode(true); c.classList.add('ftc__slide-clone'); c.setAttribute('aria-hidden', 'true'); this.quiet(c); this.track.appendChild(c); });
      this.cloned = true; this.cloneCount = k;
      this.slides = Array.from(this.track.children);
      this.real = this.slides.filter((s) => !s.classList.contains('ftc__slide-clone'));
      this.index = k;
      this.track.scrollTo({ left: this.slideLeft(k), behavior: 'auto' });
      this.track.addEventListener('scrollend', () => this.wrap());
      let t; this.track.addEventListener('scroll', () => { clearTimeout(t); t = setTimeout(() => this.wrap(), 80); }, { passive: true });
    }
    wrap() {
      if (!this.cloned) return;
      const k = this.cloneCount, n = this.real.length;
      const step = this.slideLeft(1) || 1;
      const i = Math.round(this.track.scrollLeft / step);
      if (this.track.matches('.is-dragging, .is-gliding')) return;
      if (Math.abs(this.track.scrollLeft - this.slideLeft(i)) > 2) return;
      if (i < k) { this.index = i + n; this.track.scrollTo({ left: this.slideLeft(this.index), behavior: 'auto' }); this.update(); }
      else if (i >= k + n) { this.index = i - n; this.track.scrollTo({ left: this.slideLeft(this.index), behavior: 'auto' }); this.update(); }
    }
    get realIndex() { return this.cloned ? ((this.index - this.cloneCount) % this.real.length + this.real.length) % this.real.length : this.index; }

    get isSlider() { return getComputedStyle(this.track).display !== 'grid'; }
    get pages() { return Math.max(1, Math.ceil(this.slides.length - Math.floor(this.perView) + 1)); }
    slideLeft(i) { return this.slides[i].offsetLeft - this.slides[0].offsetLeft; }

    go(i, { instant = false, velocity = null } = {}) {
      if (!this.ensure()) return;
      const last = this.pages - 1;
      if (this.loop) i = i < 0 ? last : i > last ? 0 : i; else i = Math.max(0, Math.min(last, i));
      this.glide(this.slideLeft(i), { instant: REDUCED || instant, velocity });
      this.index = i; this.update(); this._start = performance.now();
      if (this.perView <= 1) this.slides.forEach((s, k) => { if (s.classList.contains('ftc__slide-clone')) return; s.querySelectorAll('ft-video[autoplay]').forEach((v) => k === i ? v.play() : v.pause()); });
    }

    stopGlide() {
      if (!this.track.classList.contains('is-gliding')) return;
      cancelAnimationFrame(this._glide);
      this.track.classList.remove('is-gliding');
    }

    // velocity (px/ms, from a drag release): the glide starts at the hand's speed instead of jumping ahead of it.
    glide(left, { instant = false, velocity = null } = {}) {
      const t = this.track;
      cancelAnimationFrame(this._glide);
      const max = t.scrollWidth - t.clientWidth;
      const to = Math.max(0, Math.min(left, max));
      const from = t.scrollLeft, dist = to - from;
      if (instant || Math.abs(dist) < 1) {
        t.classList.remove('is-gliding');
        t.scrollTo({ left: to, behavior: 'auto' });
        t.dispatchEvent(new Event('scrollend'));
        return;
      }
      if (!this._glideBound) {
        this._glideBound = true;
        t.addEventListener('wheel', () => this.stopGlide(), { passive: true });
      }
      const span = Math.abs(dist);
      let duration = Math.min(650, Math.max(400, 350 + span * 0.15));
      // Cubic with start slope k and a soft landing; k = 1 starts gently (like CSS ease), k = 3 is the fastest without overshoot.
      let k = 1;
      if (velocity !== null) {
        const v = velocity * Math.sign(dist);
        if (v <= 0) k = 0;
        else {
          if ((v * duration) / span > 3) duration = Math.max(200, (3 * span) / v);
          k = Math.min(3, (v * duration) / span);
        }
      }
      const ease = (p) => ((k - 2) * p + (3 - 2 * k)) * p * p + k * p;
      t.classList.add('is-gliding');
      let t0 = 0;
      const frame = (now) => {
        if (!t0) t0 = now;
        const p = Math.min(1, (now - t0) / duration);
        t.scrollLeft = from + dist * ease(p);
        if (p < 1) { this._glide = requestAnimationFrame(frame); return; }
        t.classList.remove('is-gliding');
        t.dispatchEvent(new Event('scrollend'));
      };
      this._glide = requestAnimationFrame(frame);
    }

    onScroll() {
      clearTimeout(this._scrollT);
      this._scrollT = setTimeout(() => {
        if (this.track.matches('.is-gliding, .is-dragging')) return;
        this.settle();
        const x = this.track.scrollLeft; let best = 0, bestD = Infinity;
        this.slides.forEach((s, i) => { const d = Math.abs(this.slideLeft(i) - x); if (d < bestD) { bestD = d; best = i; } });
        best = Math.min(best, this.pages - 1);
        if (best !== this.index) { this.index = best; this.update(); }
      }, 60);
    }

    enableDrag() {
      enableDragScroll(this.track, {
        axis: 'x',
        settle: true,
        touch: true,
        // data-select-text (slideshow): headings and text can be selected with the mouse instead of dragging slides
        noDrag: this.hasAttribute('data-select-text') ? '.text-block' : null,
        onStart: () => this.stopGlide(),
        onEnd: (x, from, v = 0) => {
          const step = this.slideLeft(1) || 1;
          const origin = Math.round(from / step);
          const moved = x - from;
          const flick = Math.abs(v) > 0.3 && Math.abs(moved) > 10 && Math.sign(v) === Math.sign(moved);
          const threshold = Math.min(50, step * 0.15);
          if (!flick && Math.abs(moved) < threshold) { this.go(origin, { velocity: v }); return; }
          const reach = Math.abs(moved) + (flick ? Math.min(Math.abs(v), 4) * 120 : 0);
          const count = Math.max(1, Math.round(reach / step));
          this.go(origin + Math.sign(moved) * count, { velocity: v });
        },
      });
    }

    buildDots() {
      if (!this.dots || this.dotsBound) return;
      this.dotsBound = true;
      this.dots.querySelectorAll('.ftc__dot').forEach((b, i) => b.addEventListener('click', () => this.go(this.cloned ? i + this.cloneCount : i)));
    }

    update() {
      const n = this.cloned ? this.real.length : this.pages;
      const cur = this.realIndex;
      this.dots?.querySelectorAll('.ftc__dot').forEach((d, i) => { d.hidden = i >= n; d.setAttribute('aria-current', i === cur ? 'true' : 'false'); });
      if (this.cur) this.cur.textContent = String(cur + 1).padStart(2, '0');
      if (this.total) this.total.textContent = String(n).padStart(2, '0');
      if (this.bar) this.bar.style.transform = `scaleX(${(cur + 1) / n})`;
      this.prevs.forEach((b) => { b.disabled = !this.loop && this.index === 0; });
      this.nexts.forEach((b) => { b.disabled = !this.loop && this.index === n - 1; });
      // The slide being left keeps data-leaving until the track settles, so it stays styled as shown while it slides out.
      this.slides.forEach((s, i) => {
        const on = i === this.index;
        if (!on && s.hasAttribute('data-active') && this.track.matches('.is-gliding')) s.setAttribute('data-leaving', '');
        if (on) s.removeAttribute('data-leaving');
        s.toggleAttribute('data-active', on);
      });
      if (!this._settleBound) { this._settleBound = true; this.track.addEventListener('scrollend', () => { if (!this.track.matches('.is-gliding, .is-dragging')) this.settle(); }); }
      this.dispatchEvent(new CustomEvent('ftc:change', { detail: { index: cur } }));
    }

    settle() { this.track?.querySelectorAll(':scope > [data-leaving]').forEach((s) => s.removeAttribute('data-leaving')); }

    play() {
      if (!this.delay || this.playing || !this.isSlider || this.hasAttribute('data-lock')) return;
      if (!this.inputSeen) {
        this.wantsPlay = true;
        if (!this.waitingInput) {
          this.waitingInput = true;
          FIRST_INPUT.then(() => { this.inputSeen = true; if (this.wantsPlay) this.play(); });
        }
        return;
      }
      this.playing = true; this._start = performance.now() - (this._elapsed || 0);
      cancelAnimationFrame(this._raf); this._raf = requestAnimationFrame((t) => this.tick(t));
    }
    pause() { this.wantsPlay = false; if (this.playing) this._elapsed = performance.now() - this._start; this.playing = false; cancelAnimationFrame(this._raf); }
    tick(now) {
      if (!this.playing) return;
      const p = Math.min(1, (now - this._start) / this.delay);
      if (p >= 1) { this._elapsed = 0; this._start = now; const last = this.cloned ? this.slides.length - 1 : this.pages - 1; this.go(this.index >= last && !this.loop ? 0 : this.index + 1); }
      this._raf = requestAnimationFrame((t) => this.tick(t));
    }
  }
  safeDefine('ft-carousel', FtCarousel);

  /* ---- <ft-tabs> ---- */
  class FtTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.indicator = this.querySelector('.ftt__indicator');
      const moveIndicator = (animate) => {
        const t = this.tabs.find((x) => x.getAttribute('aria-selected') === 'true'); if (!t || !this.indicator) return;
        const to = { transform: `translateX(${t.offsetLeft}px)`, width: `${t.offsetWidth}px` };
        if (animate && M && !REDUCED) M.animate(this.indicator, to, { duration: 0.45, easing: [0.22, 1, 0.36, 1] });
        else Object.assign(this.indicator.style, to);
      };
      this.moveIndicator = moveIndicator;
      moveIndicator(false);
      window.addEventListener('resize', () => moveIndicator(false));
      if (M && !REDUCED) {
        M.animate(this.tabs, { opacity: 0, transform: 'translateY(10px)' }, { duration: 0 });
        const active = this.tabs.find((x) => x.getAttribute('aria-selected') === 'true');
        const cards = active ? this.panel(active).querySelectorAll('.card') : [];
        M.animate(cards, { opacity: 0, transform: 'translateY(30px)' }, { duration: 0 });
        M.inView(this, () => {
          M.animate(this.tabs, { opacity: 1, transform: 'translateY(0)' }, { duration: 0.5, delay: M.stagger(0.06), easing: EASE });
          mediaLoaded(this).then(() => M.animate(cards, { opacity: 1, transform: 'translateY(0)' }, { duration: 0.6, delay: M.stagger(0.06, { start: 0.15 }), easing: EASE }));
        }, { margin: '0px 0px -10% 0px' });
      }
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight') this.select((i + 1) % this.tabs.length, true);
          if (e.key === 'ArrowLeft')  this.select((i - 1 + this.tabs.length) % this.tabs.length, true);
        });
      });
    }
    panel(tab) { return document.getElementById(tab.getAttribute('aria-controls')); }
    async select(i, focus) {
      const current = this.tabs.find((t) => t.getAttribute('aria-selected') === 'true');
      const next = this.tabs[i];
      if (current === next) return;
      const from = this.panel(current), to = this.panel(next);
      this.tabs.forEach((t) => { t.setAttribute('aria-selected', t === next ? 'true' : 'false'); t.tabIndex = t === next ? 0 : -1; });
      this.moveIndicator?.(true);
      if (focus) next.focus();
      if (M && !REDUCED) await M.animate(from, { opacity: [1, 0], transform: ['translateY(0)', 'translateY(1rem)'] }, { duration: 0.18 }).finished;
      from.hidden = true; to.hidden = false;
      if (M && !REDUCED) {
        M.animate(to, { opacity: [0, 1], transform: ['translateY(1rem)', 'translateY(0)'] }, { duration: 0.25, easing: EASE });
        const cards = to.querySelectorAll('.card');
        mediaLoaded(to).then(() => M.animate(cards, { opacity: [0, 1], transform: ['translateY(30px)', 'translateY(0)'] }, { duration: 0.5, easing: EASE, delay: M.stagger(0.06) }));
      }
    }
  }
  safeDefine('ft-tabs', FtTabs);

  /* ---- <ft-accordion> ---- */
  class FtAccordion extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('details'));
      this.items.forEach((d) => {
        const summary = d.querySelector('summary');
        summary.addEventListener('click', (e) => { e.preventDefault(); this.toggle(d); });
      });
    }
    async toggle(d) {
      const summaryH = d.querySelector('summary').offsetHeight;
      if (d.open) {
        if (M && !REDUCED) await M.animate(d, { height: [`${d.scrollHeight}px`, `${summaryH}px`] }, { duration: 0.28, easing: 'ease' }).finished;
        d.open = false; d.style.height = '';
      } else {
        if (this.hasAttribute('data-single')) this.items.forEach((o) => o !== d && o.open && this.toggle(o));
        d.open = true;
        if (M && !REDUCED) {
          const body = d.querySelector('.fta__body');
          M.animate(d, { height: [`${summaryH}px`, `${d.scrollHeight}px`] }, { duration: 0.28, easing: 'ease' }).finished.then(() => d.style.height = '');
          M.animate(body, { opacity: [0, 1], transform: ['translateY(8px)', 'translateY(0)'] }, { duration: 0.25, delay: 0.1 });
        }
      }
    }
  }
  safeDefine('ft-accordion', FtAccordion);

  /* ---- <ft-collapse> ---- */
  class FtCollapse extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('[data-collapse-toggle]');
      this.panel = this.querySelector('[data-collapse-panel]');
      if (!this.toggle || !this.panel) return;
      const below = Number(this.dataset.collapseBelow) || 750;
      this.mq = window.matchMedia(`(max-width: ${below - 0.02}px)`);
      this.onMedia = () => this.sync();
      this.mq.addEventListener('change', this.onMedia);
      this.toggle.addEventListener('click', () => this.set(!this.hasAttribute('open')));
      this.sync();
      if (document.readyState === 'complete') requestAnimationFrame(() => this.sync());
      else window.addEventListener('load', this.onMedia, { once: true });
    }
    disconnectedCallback() { this.mq?.removeEventListener('change', this.onMedia); }
    sync() {
      const small = this.mq.matches;
      this.toggle.disabled = !small;
      this.set(small ? this.opened === true : true, false);
    }
    set(open, animate = true) {
      if (this.mq.matches) this.opened = open;
      this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (!(animate && M && !REDUCED)) { this.toggleAttribute('open', open); this.panel.style.height = ''; return; }
      if (open) this.toggleAttribute('open', true);
      const full = `${this.panel.scrollHeight}px`;
      M.animate(this.panel, { height: open ? ['0px', full] : [full, '0px'], opacity: open ? [0, 1] : [1, 0] }, { duration: open ? 0.28 : 0.22, easing: 'ease' })
        .finished.then(() => { this.panel.style.height = ''; this.panel.style.opacity = ''; if (!open) this.toggleAttribute('open', false); });
    }
  }
  safeDefine('ft-collapse', FtCollapse);

  /* ---- <ft-to-top> ---- */
  class FtToTop extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button) return;
      this.after = Number(this.dataset.after) || 600;
      this.onScroll = () => { this.hidden = window.scrollY < this.after; };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.button.addEventListener('click', () => {
        if (window.lenis) window.lenis.scrollTo(0);
        else window.scrollTo({ top: 0, behavior: REDUCED ? 'auto' : 'smooth' });
      });
      this.onScroll();
    }
    disconnectedCallback() { window.removeEventListener('scroll', this.onScroll); }
  }
  safeDefine('ft-to-top', FtToTop);

  /* ---- <ft-embed> ---- */
  class FtEmbed extends HTMLElement {
    get host() { return this.getAttribute('host') === 'vimeo' ? 'vimeo' : 'youtube'; }
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.template = this.querySelector('template:not([data-placeholder])');
      this.iframe = this.querySelector('.media > iframe');
      const playBtn = this.querySelector('.ft-play');
      playBtn?.addEventListener('click', (e) => { e.stopPropagation(); this.hasAttribute('playing') ? this.pause() : this.play(); labelPlayButton(playBtn, this.hasAttribute('playing')); });
      if (playBtn) this.addEventListener('click', (e) => {
        if (e.target.closest('a, button, input, select, textarea, label')) return;
        playBtn.click();
      });
      const muteBtn = this.querySelector('.ft-mute');
      muteBtn?.addEventListener('click', (e) => { e.stopPropagation(); const unmute = !this.hasAttribute('unmuted'); this.toggleAttribute('unmuted', unmute); this.command(unmute ? 'unMute' : 'mute'); muteBtn.setAttribute('aria-label', unmute ? 'Mute' : 'Unmute'); });
      if (this.hasAttribute('autoplay')) {
        if (M) M.inView(this, () => { this.play(); return () => this.command('pause'); }, { margin: '0px 0px -10% 0px' });
        else this.play();
      }
    }
    command(name) {
      if (!this.iframe) return;
      const yt = { play: 'playVideo', pause: 'pauseVideo', mute: 'mute', unMute: 'unMute' }[name];
      const msg = this.host === 'vimeo'
        ? JSON.stringify(name === 'mute' ? { method: 'setVolume', value: 0 } : name === 'unMute' ? { method: 'setVolume', value: 1 } : { method: name })
        : JSON.stringify({ event: 'command', func: yt, args: [] });
      this.iframe.contentWindow?.postMessage(msg, '*');
    }
    play() {
      if (!this.iframe) {
        if (!this.template) return;
        this.querySelector('.media').appendChild(this.template.content.cloneNode(true));
        this.iframe = this.querySelector('iframe');
      } else this.command('play');
      this.setAttribute('playing', '');
    }
    pause() { this.command('pause'); this.removeAttribute('playing'); }
    stop()  { if (this.hasAttribute('data-inline')) return this.pause(); if (this.iframe) { this.iframe.remove(); this.iframe = null; } this.removeAttribute('playing'); }
  }
  safeDefine('ft-embed', FtEmbed);

  /* ---- <ft-model> ---- */
  class FtModel extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.template = this.querySelector('template');
      this.querySelector('.ft-3d')?.addEventListener('click', (e) => { e.stopPropagation(); this.play(); });
      this.querySelector('.ft-close')?.addEventListener('click', (e) => { e.stopPropagation(); this.pause(); });
      ['pointerdown', 'mousedown', 'touchstart', 'keydown', 'wheel', 'click'].forEach((type) => this.addEventListener(type, (e) => {
        if (this.hasAttribute('playing') && e.target.closest?.('model-viewer, .shopify-model-viewer-ui')) e.stopPropagation();
      }, { passive: true }));
    }
    play() {
      if (!this.viewer && this.template) {
        this.querySelector('.media').appendChild(this.template.content.cloneNode(true));
        this.viewer = this.querySelector('model-viewer');
        window.Shopify?.loadFeatures?.([{ name: 'model-viewer-ui', version: '1.0', onLoad: (err) => {
          if (err || this.ui || !window.Shopify.ModelViewerUI) return;
          this.ui = new window.Shopify.ModelViewerUI(this.viewer);
          if (this.hasAttribute('playing')) this.ui.play?.();
        } }]);
      }
      this.ui?.play?.();
      this.setAttribute('playing', '');
    }
    pause() {
      if (document.fullscreenElement && this.contains(document.fullscreenElement)) document.exitFullscreen?.().catch(() => {});
      this.ui?.pause?.();
      this.removeAttribute('playing');
    }
  }
  safeDefine('ft-model', FtModel);

  /* ---- <ft-header> ---- */
  class FtHeader extends HTMLElement {
    static DELTA = 8;

    connectedCallback() {
      this.behavior = this.dataset.behavior || 'on-scroll-down';
      this.sticky = this.dataset.sticky !== 'false';
      this.section = this.closest('.shopify-section-header') || this.parentElement;
      this.stuck = false;

      if (window.ResizeObserver) {
        this.observer = new ResizeObserver(() => { this.measure(); this.onScroll?.(); });
        this.observer.observe(this);
        for (let el = this.section.previousElementSibling; el; el = el.previousElementSibling) this.observer.observe(el);
      } else this.measure();
      this.onResize = () => this.measure();
      window.addEventListener('resize', this.onResize, { passive: true });
      document.addEventListener('prerenderingchange', this.onResize, { once: true });
      window.addEventListener('pageshow', this.onResize);
      window.addEventListener('load', this.onResize, { once: true });

      this.pivot = Math.max(0, window.scrollY);
      this.down = null;

      let ticking = false;
      const read = () => {
        ticking = false;
        const y = Math.max(0, window.scrollY);

        if (!this.sticky) this.unstick();
        else if (!this.stuck && y > this.top + this.height) this.stick();
        else if (this.stuck && y <= this.top) this.unstick();
        this.classList.toggle('is-scrolled', this.stuck);

        if (this.down === null) {
          if (y > this.pivot + FtHeader.DELTA) { this.down = true; this.pivot = y; }
          else if (y < this.pivot - FtHeader.DELTA) { this.down = false; this.pivot = y; }
        } else if (this.down) {
          if (y > this.pivot) this.pivot = y;
          else if (y < this.pivot - FtHeader.DELTA) { this.down = false; this.pivot = y; }
        } else {
          if (y < this.pivot) this.pivot = y;
          else if (y > this.pivot + FtHeader.DELTA) { this.down = true; this.pivot = y; }
        }

        let hide = false;
        if (this.stuck && this.down !== null && !this.classList.contains('is-open')) {
          if (this.behavior === 'on-scroll-up') hide = this.down;
          else if (this.behavior === 'on-scroll-down') hide = !this.down;
        }
        this.toggleAttribute('hidden-up', hide);
      };
      this.onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(read); } };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      read();

      this.onPanel = () => this.sync();
      this.addEventListener('ft:panel', this.onPanel);

      this.nav = this.querySelector('.header__nav');
      this.onNavKeys = (e) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
        const row = Array.from(this.nav.querySelectorAll(':scope > .nav-item'))
          .map((el) => (el.matches('a') ? el : el.querySelector(':scope > summary')))
          .filter(Boolean);
        const i = row.indexOf(e.target);
        if (i < 0 || row.length < 2) return;
        e.preventDefault();
        const to = e.key === 'Home' ? 0
          : e.key === 'End' ? row.length - 1
          : (i + (e.key === 'ArrowRight' ? 1 : -1) + row.length) % row.length;
        row[to].focus();
      };
      this.nav?.addEventListener('keydown', this.onNavKeys);
    }
    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('pageshow', this.onResize);
      this.nav?.removeEventListener('keydown', this.onNavKeys);
      this.observer?.disconnect();
    }
    sync() {
      const open = !!this.querySelector('ft-mega[open], .header__nav > ft-dropdown[open]');
      this.classList.toggle('is-open', open);
      if (open) this.removeAttribute('hidden-up');
    }
    measure() {
      const h = this.offsetHeight;
      if (!this.stuck && h) {
        this.top = this.section.getBoundingClientRect().top + window.scrollY;
        this.height = h;
      }
      if (h && h !== this.published) {
        this.published = h;
        document.documentElement.style.setProperty('--header-height', `${h}px`);
      }
      this.top ??= 0;
      this.height ??= h;
    }
    stick() {
      this.stuck = true;
      this.style.transition = 'none';
      this.setAttribute('hidden-up', '');
      this.section.classList.add('is-stuck');
      void this.offsetHeight;
      this.style.transition = '';
    }
    unstick() {
      if (!this.stuck) return;
      this.stuck = false;
      this.section.classList.remove('is-stuck');
      this.style.transition = 'none';
      this.removeAttribute('hidden-up');
      void this.offsetHeight;
      this.style.transition = '';
    }
  }
  safeDefine('ft-header', FtHeader);

  /* ---- Navigation keyboard ---- */
  const PANEL_FOCUSABLE = 'a[href], summary[tabindex], button:not([disabled]), input:not([type="hidden"])';

  const HOVER_OPEN_MS = 150, HOVER_CLOSE_MS = 300;
  function navHover(host) {
    host.addEventListener('mouseenter', () => {
      clearTimeout(host._closeT);
      clearTimeout(host._openT);
      // Hover intent: while a sibling menu is open, wait for the pointer to settle, so a diagonal move toward that
      // menu's panel does not swap it for whichever item the pointer crosses on the way.
      const nav = host.closest('.header__nav');
      const siblings = host.hasAttribute('data-sub')
        ? host.parentElement?.querySelectorAll(':scope > ft-dropdown[open]')
        : nav?.querySelectorAll(':scope > :is(ft-mega, ft-dropdown)[open]');
      const other = siblings && Array.from(siblings).find((m) => m !== host);
      if (!other) { host.open(); return; }
      const wait = () => {
        clearTimeout(host._openT);
        host._openT = setTimeout(() => {
          host.removeEventListener('mousemove', wait);
          if (host.matches(':hover')) host.open();
        }, HOVER_OPEN_MS);
      };
      host._wait = wait;
      host.addEventListener('mousemove', wait);
      wait();
    });
    host.addEventListener('mouseleave', () => {
      clearTimeout(host._openT);
      if (host._wait) host.removeEventListener('mousemove', host._wait);
      clearTimeout(host._closeT);
      host._closeT = setTimeout(() => {
        if (host.matches(':hover') || host.panel.contains(document.activeElement)) return;
        host.close();
      }, HOVER_CLOSE_MS);
    });
  }

  function navTriggerKeys(host) {
    const enter = () => setTimeout(() => host.panel?.querySelector(PANEL_FOCUSABLE)?.focus(), 60);
    const back = () => { host.close(); host.summary.focus(); };
    const sub = host.hasAttribute('data-sub');

    host.summary.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        host.toggle();
      } else if (e.key === 'ArrowDown' || (sub && e.key === 'ArrowRight')) {
        e.preventDefault();
        host.open();
        enter();
      } else if (sub && e.key === 'ArrowLeft' && host.hasAttribute('open')) {
        e.preventDefault();
        host.close();
      }
    });

    host.addEventListener('keydown', (e) => {
      if (!host.hasAttribute('open')) return;
      if (e.key === 'Escape' || (sub && e.key === 'ArrowLeft' && e.target !== host.summary)) {
        e.preventDefault();
        e.stopPropagation();
        back();
      }
    });

    host.addEventListener('focusout', (e) => {
      if (!host.hasAttribute('open')) return;
      if (host.contains(e.relatedTarget) || host.matches(':hover')) return;
      host.close();
    });
  }

  /* ---- <ft-mega> ---- */
  class FtMega extends HTMLElement {
    static DURATION_MS = 400;

    connectedCallback() {
      this.summary = this.querySelector('summary');
      this.panel = this.querySelector('.mega');
      this.panel.inert = true;
      this.summary.addEventListener('click', (e) => { e.preventDefault(); this.toggle(); });
      navTriggerKeys(this);
      if (window.matchMedia('(hover: hover)').matches) {
        navHover(this);
      }
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.close(); });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    }
    toggle() { this.hasAttribute('open') ? this.close() : this.open(); }
    async open() {
      if (this.hasAttribute('open')) return;
      clearTimeout(this._hideT);
      document.querySelectorAll('ft-mega[open]').forEach((m) => m !== this && m.close());
      this.closest('.header__nav')?.querySelectorAll(':scope > ft-dropdown[open]').forEach((d) => d.close());
      this.panel.querySelectorAll('template[data-defer]').forEach((t) => t.replaceWith(t.content.cloneNode(true)));
      this.panel.style.display = 'block';
      this.panel.inert = false;
      const bar = this.closest('.header');
      if (bar) this.summary.style.setProperty('--nav-bridge', `${Math.max(0, Math.ceil(bar.getBoundingClientRect().bottom - this.summary.getBoundingClientRect().bottom))}px`);
      void this.panel.offsetHeight;
      this.setAttribute('open', '');
      this.summary.setAttribute('aria-expanded', 'true');
      this.dispatchEvent(new CustomEvent('ft:panel', { bubbles: true }));
      const items = this.panel.querySelectorAll('.mega__col, .mega__promo, .mega__feature, .plist-card');
      if (M && !REDUCED) {
        M.animate(items, { opacity: [0, 1], transform: ['translateY(14px)', 'translateY(0)'] }, { duration: 0.5, delay: M.stagger(0.06, { start: 0.15 }), easing: EASE });
      }
    }
    async close() {
      if (!this.hasAttribute('open')) return;
      this.summary.setAttribute('aria-expanded', 'false');
      this.panel.inert = true;
      this.removeAttribute('open');
      this.dispatchEvent(new CustomEvent('ft:panel', { bubbles: true }));
      clearTimeout(this._hideT);
      this._hideT = setTimeout(() => {
        if (!this.hasAttribute('open')) this.panel.style.display = 'none';
      }, REDUCED ? 0 : FtMega.DURATION_MS);
    }
  }
  safeDefine('ft-mega', FtMega);

  /* ---- <ft-expand> ---- */
  class FtExpand extends HTMLElement {
    static DURATION_MS = 450;

    connectedCallback() {
      this.inner = this.querySelector('.ft-expand__inner') || this.firstElementChild;
      if (!this.inner) return;
      this.adopt();
      this.triggers.forEach((t) => t.addEventListener('click', (e) => { e.preventDefault(); this.toggle(t); }));
      this.querySelectorAll('[data-expand-close]').forEach((b) => b.addEventListener('click', () => this.close()));
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.hasAttribute('open')) this.close(); });
      if (DESIGN) {
        const section = this.closest('.shopify-section');
        section?.addEventListener('shopify:section:select', () => this.open());
        section?.addEventListener('shopify:block:select', (e) => { if (this.contains(e.target)) this.open(); });
        section?.addEventListener('shopify:section:deselect', () => this.close());
      }
    }
    get triggers() { return Array.from(document.querySelectorAll(`[data-expand="${this.id}"]`)); }

    adopt() {
      const selector = this.getAttribute('data-adopt');
      const target = this.querySelector('[data-adopt-target]');
      if (!selector || !target) return;
      const scope = this.closest('.shopify-section') || document;
      this.sources = [];
      scope.querySelectorAll(`template${selector}`).forEach((tpl, i) => {
        target.appendChild(tpl.content.cloneNode(true));
        this.sources.push(tpl.parentElement);
        const trigger = tpl.parentElement?.querySelector(`[data-expand="${this.id}"]`);
        if (trigger) trigger.dataset.expandIndex = i;
      });
      const carousel = target.closest('ft-carousel');
      if (typeof carousel?.refresh === 'function') carousel.refresh();
      this.link(carousel);
    }

    // Keep the panel's carousel on the same announcement as the bar's carousel (autoplay, arrows, swipe), both ways.
    // Bar slides without a panel (plain announcements) leave the panel where it is.
    link(carousel) {
      const bar = this.sources[0]?.closest('ft-carousel');
      if (!carousel || !bar || bar === carousel) return;
      const barSlides = () => Array.from(bar.querySelector('.ftc__track')?.children || []).filter((s) => !s.classList.contains('ftc__slide-clone'));
      bar.addEventListener('ftc:change', (e) => {
        const i = this.sources.indexOf(barSlides()[e.detail.index]);
        if (i > -1 && carousel.realIndex !== i) carousel.go(i, { instant: !this.hasAttribute('open') });
      });
      carousel.addEventListener('ftc:change', (e) => {
        if (!this.hasAttribute('open')) return;
        const i = barSlides().indexOf(this.sources[e.detail.index]);
        if (i > -1 && bar.realIndex !== i) bar.go(i);
      });
    }

    toggle(trigger) {
      if (!this.hasAttribute('open')) return this.open(trigger);
      const i = parseInt(trigger?.dataset?.expandIndex ?? '', 10);
      const carousel = this.querySelector('ft-carousel');
      if (!Number.isNaN(i) && carousel && carousel.realIndex !== i) return this.showSlide(trigger);
      this.close();
    }

    showSlide(trigger) {
      const i = parseInt(trigger?.dataset?.expandIndex ?? '', 10);
      if (Number.isNaN(i)) return;
      const carousel = this.querySelector('ft-carousel');
      if (typeof carousel?.go === 'function') carousel.go(i);
    }
    sync(open) {
      this.toggleAttribute('open', open);
      this.toggleAttribute('inert', !open);
      this.triggers.forEach((t) => t.setAttribute('aria-expanded', open ? 'true' : 'false'));
    }
    open(trigger) {
      if (this.hasAttribute('open')) {
        this.showSlide(trigger);
        return;
      }
      clearTimeout(this._settle);
      this.sync(true);
      this.style.height = 'auto';
      const target = this.offsetHeight;
      this.showSlide(trigger);
      if (M && !REDUCED) {
        this.style.height = '0px';
        void this.offsetHeight;
        this.style.height = `${target}px`;
        M.animate(this.inner, { opacity: [0, 1], transform: ['translateY(-24px)', 'translateY(0)'] }, { duration: 0.55, easing: EASE });
        this._settle = setTimeout(() => { if (this.hasAttribute('open')) this.style.height = 'auto'; }, FtExpand.DURATION_MS + 60);
      }
      this.dispatchEvent(new CustomEvent('ft:expanded'));
    }
    async close() {
      if (!this.hasAttribute('open')) return;
      clearTimeout(this._settle);
      this.triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
      if (M && !REDUCED) {
        this.style.height = `${this.offsetHeight}px`;
        void this.offsetHeight;
        M.animate(this.inner, { opacity: 0 }, { duration: 0.2 });
        this.style.height = '0px';
        await new Promise((done) => { this._settle = setTimeout(done, FtExpand.DURATION_MS + 40); });
      }
      this.sync(false);
      this.style.height = '';
      this.inner.style.opacity = '';
      this.inner.style.transform = '';
      this.dispatchEvent(new CustomEvent('ft:collapsed'));
    }
  }
  safeDefine('ft-expand', FtExpand);

  /* ---- Modal layer: focus trap, inert page, scroll lock ---- */
  // Every modal drawer and dialog opens through here, so they all behave the same:
  // the rest of the page goes inert, Tab and Shift+Tab loop inside the dialog,
  // Escape closes only the topmost one, and focus goes back to whatever opened it.
  // Dialogs stack (a size guide inside quick view); the page unlocks when the last one closes.
  const modal = (() => {
    const FOCUSABLE = 'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, summary, [contenteditable="true"], [tabindex]:not([tabindex="-1"])';
    const SKIP = new Set(['SCRIPT', 'STYLE', 'LINK', 'TEMPLATE', 'NOSCRIPT']);
    const stack = [];
    const ours = new Set();

    const focusables = (root) => Array.from(root.querySelectorAll(FOCUSABLE)).filter((el) =>
      !el.closest('[inert]') && el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden');

    // Inert every sibling along the path from the top dialog up to <body>, so only the dialog stays reachable.
    // Elements that were already inert are left alone, and only what was set here is undone.
    const isolate = () => {
      ours.forEach((el) => { el.inert = false; });
      ours.clear();
      const top = stack[stack.length - 1];
      if (!top) return;
      for (let node = top.el; node && node !== document.body && node.parentElement; node = node.parentElement) {
        for (const sib of node.parentElement.children) {
          // data-modal-keep: live regions (toasts) that must still be announced over a dialog.
          if (sib === node || SKIP.has(sib.tagName) || sib.inert || sib.hasAttribute('data-modal-keep')) continue;
          sib.inert = true;
          ours.add(sib);
        }
      }
    };

    const lock = (on) => {
      document.documentElement.classList.toggle('scroll-locked', on);
      if (on) window.lenis?.stop(); else window.lenis?.start();
    };

    const boxOf = (entry) => entry.panel || entry.el;

    document.addEventListener('keydown', (e) => {
      const top = stack[stack.length - 1];
      if (!top) return;
      // Focus in a layer opened on top by something else (PhotoSwipe zoom, Shop Pay): that layer owns its keys.
      const active = document.activeElement;
      if (active && active !== document.body && !top.el.contains(active) && !active.closest('[inert]')) return;
      if (e.key === 'Escape') {
        // A control inside that handled Escape itself (an open select list) keeps the dialog open.
        if (e.defaultPrevented) return;
        e.preventDefault();
        top.onEscape?.();
        return;
      }
      if (e.key !== 'Tab') return;
      const box = boxOf(top);
      const list = focusables(box);
      if (!list.length) { e.preventDefault(); box.focus(); return; }
      const first = list[0], last = list[list.length - 1];
      const outside = !box.contains(active);
      if (e.shiftKey && (outside || active === first || active === box)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && (outside || active === last)) { e.preventDefault(); first.focus(); }
    });

    // No focusin pull-back: payment popups (Shop Pay, PayPal) appended to <body> while the cart or quick view is open must keep focus.

    return {
      focusables,
      get top() { return stack[stack.length - 1]?.el || null; },
      isOpen: (el) => stack.some((s) => s.el === el),
      // el: the dialog; panel: the part holding its content; trigger: gets focus back on close; onEscape: what Escape does.
      open(el, { panel = el, trigger = document.activeElement, onEscape } = {}) {
        if (this.isOpen(el)) return;
        if (!panel.hasAttribute('tabindex')) panel.setAttribute('tabindex', '-1');
        stack.push({ el, panel, trigger, onEscape });
        isolate();
        lock(true);
      },
      focusFirst(el) {
        const entry = stack.find((s) => s.el === el);
        if (!entry) return;
        const box = boxOf(entry);
        (box.querySelector('[data-autofocus]') || focusables(box)[0] || box).focus({ preventScroll: true });
      },
      close(el, { restoreFocus = true } = {}) {
        const i = stack.findIndex((s) => s.el === el);
        if (i < 0) return;
        const [entry] = stack.splice(i, 1);
        isolate();
        if (!stack.length) lock(false);
        const back = entry.trigger;
        const top = stack[stack.length - 1];
        if (restoreFocus && back?.isConnected && !back.closest('[inert]') && (!top || top.el.contains(back))) back.focus({ preventScroll: true });
        else if (top) this.focusFirst(top.el);
      }
    };
  })();
  window.theme = Object.assign(window.theme || {}, { modal });

  /* ---- <ft-drawer> / <ft-modal> ---- */
  class FtOverlay extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.backdrop = this.querySelector('.ft-backdrop');
      this.panel = this.querySelector('.ft-panel');
      this.querySelectorAll('[data-close]').forEach((b) => b.addEventListener('click', () => this.close()));
      this.backdrop?.addEventListener('click', () => this.close());
      this.syncRole();
      const delay = parseInt(this.getAttribute('data-auto-open'));
      let seen = false; try { seen = !!sessionStorage.getItem(`ft-seen-${this.id}`); } catch {}
      if (delay && !DESIGN && !seen) setTimeout(() => this.open(), delay);
    }
    // Removed while open (a section re-rendered in the editor): drop it from the modal stack, or the page stays inert and locked.
    disconnectedCallback() {
      if (!modal.isOpen(this)) return;
      this.closing = false;
      this.removeAttribute('open');
      modal.close(this, { restoreFocus: false });
    }
    // A modal dialog everywhere, except a drawer shown as a plain sidebar on desktop (vertical filters).
    syncRole() {
      const apply = () => {
        if (this.isStatic) {
          this.removeAttribute('role'); this.removeAttribute('aria-modal');
          if (this.hasAttribute('open')) this.close({ instant: true });
        } else {
          this.setAttribute('role', 'dialog'); this.setAttribute('aria-modal', 'true');
        }
      };
      apply();
      if (this.dataset.staticDesktop === 'true') FtOverlay.DESKTOP.addEventListener('change', apply);
    }
    get isStatic() { return this.dataset.staticDesktop === 'true' && FtOverlay.DESKTOP.matches; }
    get enter() {
      if (this.tagName === 'FT-MODAL') return { from: 'translate(-50%,-45%) scale(.96)', to: 'translate(-50%,-50%) scale(1)' };
      const side = this.getAttribute('data-side');
      if (side === 'top') return { from: 'translateY(-100%)', to: 'translateY(0)' };
      return { from: `translateX(${side === 'left' ? '-100%' : '100%'})`, to: 'translateX(0)' };
    }
    open(trigger) {
      if (this.hasAttribute('open')) return;
      this.querySelectorAll('template[data-defer]').forEach((t) => t.replaceWith(t.content.cloneNode(true)));
      if (this.isStatic) return;
      this.setAttribute('open', '');
      modal.open(this, { panel: this.panel, trigger: trigger || document.activeElement, onEscape: () => this.close() });
      try { sessionStorage.setItem(`ft-seen-${this.id}`, '1'); } catch {}
      const { from, to } = this.enter;
      if (M && !REDUCED) {
        M.animate(this.backdrop, { opacity: [0, 1] }, { duration: 0.2 });
        M.animate(this.panel, { transform: [from, to], opacity: [0, 1] }, { duration: 0.32, easing: [0.22, 1, 0.36, 1] });
        const items = this.panel.querySelectorAll('[data-stagger] > *');
        if (items.length) M.animate(items, { opacity: [0, 1], transform: ['translateX(-16px)', 'translateX(0)'] }, { duration: 0.4, delay: M.stagger(0.04, { start: 0.1 }), easing: EASE });
      } else { this.backdrop.style.opacity = 1; this.panel.style.transform = to; }
      this.panel.focus({ preventScroll: true });
      setTimeout(() => { if (this.hasAttribute('open')) modal.focusFirst(this); }, 100);
    }
    async close({ instant = false } = {}) {
      if (!this.hasAttribute('open') || this.closing) return;
      this.closing = true;
      const { from, to } = this.enter;
      if (M && !REDUCED && !instant) {
        M.animate(this.backdrop, { opacity: 0 }, { duration: 0.2 });
        await M.animate(this.panel, { transform: [to, from], opacity: [1, 0] }, { duration: 0.22, easing: 'ease-in' }).finished;
      }
      if (instant) { this.panel.style.transform = ''; this.panel.style.opacity = ''; this.backdrop.style.opacity = ''; }
      this.closing = false;
      this.removeAttribute('open');
      modal.close(this);
      this.dispatchEvent(new CustomEvent('ft:closed'));
    }
  }
  FtOverlay.DESKTOP = window.matchMedia('(min-width: 990px)');
  // One delegated listener for every [data-open] trigger, so a trigger rendered after its drawer (a re-rendered
  // header, a sticky clone, content loaded later) still opens it.
  document.addEventListener('click', (e) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const trigger = e.target.closest('[data-open]');
    const overlay = trigger?.dataset.open && document.getElementById(trigger.dataset.open);
    if (!(overlay instanceof FtOverlay)) return;
    e.preventDefault();
    overlay.open(trigger);
  });
  safeDefine('ft-drawer', class extends FtOverlay {});
  safeDefine('ft-modal',  class extends FtOverlay {});

  /* ---- <ft-menu> ---- */
  class FtMenu extends HTMLElement {
    connectedCallback() {
      this.root = this.querySelector('.ftm__panel.is-root');
      this.stack = [];
      this.querySelectorAll('[data-panel-open]').forEach((b) => b.addEventListener('click', () => { const p = this.querySelector(`.ftm__panel[data-panel="${b.dataset.panelOpen}"]`); if (p) this.push(p); }));
      this.querySelectorAll('[data-panel-back]').forEach((b) => b.addEventListener('click', () => this.pop()));
      this.closest('ft-drawer')?.addEventListener('ft:closed', () => this.reset());
    }
    push(panel) {
      const prev = this.stack[this.stack.length - 1] || this.root;
      panel.ftOpener = document.activeElement;
      this.stack.push(panel);
      panel.classList.add('is-active');
      prev.inert = true;
      const dur = REDUCED || !M ? 0 : 0.4;
      if (M) {
        M.animate(panel, { transform: ['translateX(100%)', 'translateX(0)'] }, { duration: dur, easing: [0.22, 1, 0.36, 1] });
        M.animate(prev, { transform: ['translateX(0)', 'translateX(-30%)'], opacity: [1, 0.4] }, { duration: dur, easing: [0.22, 1, 0.36, 1] });
        this.stagger(panel);
      } else panel.style.transform = 'translateX(0)';
      setTimeout(() => panel.querySelector('button, a')?.focus(), dur * 1000);
    }
    stagger(panel) {
      if (!M || REDUCED) return;
      const items = panel.querySelectorAll('.ftm__list > li, .ftm__card, .ftm__promo');
      if (!items.length) return;
      M.animate(items, { opacity: [0, 1], transform: ['translateX(-16px)', 'translateX(0)'] }, { duration: 0.5, delay: M.stagger(0.05, { start: 0.15 }), easing: EASE });
    }
    async pop() {
      const panel = this.stack.pop(); if (!panel) return;
      const prev = this.stack[this.stack.length - 1] || this.root;
      const dur = REDUCED || !M ? 0 : 0.35;
      if (M) {
        M.animate(prev, { transform: ['translateX(-30%)', 'translateX(0)'], opacity: [0.4, 1] }, { duration: dur, easing: 'ease-out' });
        await M.animate(panel, { transform: ['translateX(0)', 'translateX(100%)'] }, { duration: dur, easing: 'ease-in' }).finished;
      }
      prev.inert = false;
      panel.classList.remove('is-active'); panel.style.transform = '';
      if (panel.contains(document.activeElement) || document.activeElement === document.body) panel.ftOpener?.focus({ preventScroll: true });
    }
    reset() {
      this.stack.forEach((p) => { p.classList.remove('is-active'); p.style.transform = ''; p.style.opacity = ''; p.inert = false; });
      this.stack = []; this.root.style.transform = ''; this.root.style.opacity = ''; this.root.inert = false;
    }
  }
  safeDefine('ft-menu', FtMenu);

  /* ---- <ft-dropdown> ---- */
  class FtDropdown extends HTMLElement {
    connectedCallback() {
      this.summary = this.querySelector(':scope > summary');
      this.panel = this.querySelector(':scope > .dropdown');
      if (!this.summary || !this.panel) return;
      this.summary.setAttribute('aria-expanded', 'false');
      this.panel.inert = true;
      this.summary.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); this.toggle(); });
      if (window.matchMedia('(hover: hover)').matches) {
        navHover(this);
      }
      navTriggerKeys(this);
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.close(); });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    }
    toggle() { this.hasAttribute('open') ? this.close() : this.open(); }
    open() {
      if (this.hasAttribute('open') && !this._closing) return;
      this._closing = false;
      this._anim?.cancel?.();
      this.parentElement?.querySelectorAll(':scope > ft-dropdown[open], :scope > .dropdown > ft-dropdown[open]').forEach((d) => d !== this && d.close());
      if (!this.hasAttribute('data-sub')) this.closest('.header__nav')?.querySelectorAll('ft-mega[open]').forEach((m) => m.close());
      this.setAttribute('open', ''); this.summary.setAttribute('aria-expanded', 'true');
      this.panel.inert = false;
      this.dispatchEvent(new CustomEvent('ft:panel', { bubbles: true }));
      const panel = this.panel;
      if (!M || REDUCED) return;
      this._anim = M.animate(panel, { opacity: [0, 1], transform: [FtDropdown.RAISED, 'translateY(0)'] }, { duration: 0.3, easing: [0.22, 1, 0.36, 1] });
      M.animate(
        panel.querySelectorAll(':scope > a, :scope > ft-dropdown'),
        { opacity: [0, 1], transform: ['translateY(8px)', 'translateY(0)'] },
        { duration: 0.35, delay: M.stagger(0.035, { start: 0.06 }), easing: EASE }
      );
      this._anim.finished.then(() => { panel.style.transform = ''; }).catch(() => {});
    }
    async close() {
      if (!this.hasAttribute('open') || this._closing) return;
      this._closing = true;
      this.querySelectorAll('ft-dropdown[open]').forEach((d) => d.close());
      this.summary.setAttribute('aria-expanded', 'false');
      const panel = this.panel;
      panel.inert = true;
      if (M && !REDUCED) {
        this._anim?.cancel?.();
        try {
          this._anim = M.animate(panel, { opacity: [1, 0], transform: ['translateY(0)', FtDropdown.RAISED] }, { duration: 0.2, easing: 'ease-in' });
          await this._anim.finished;
        } catch (e) { }
      }
      if (!this._closing) return;
      this._closing = false;
      this.removeAttribute('open');
      panel.style.transform = ''; panel.style.opacity = '';
      this.dispatchEvent(new CustomEvent('ft:panel', { bubbles: true }));
    }
  }
  FtDropdown.RAISED = 'translateY(-8px)';
  safeDefine('ft-dropdown', FtDropdown);

  /* ---- Email fields ---- */
  // type="email" alone lets through "name@domain" or "name@shop..com". Every email field (newsletter, contact,
  // account, gift card recipient, back in stock) gets the same full check: a local part, a domain of valid labels
  // and a top-level domain, with the reason said in the store's language instead of the browser's generic text.
  (() => {
    const EMAIL = /^[^\s@.]+(?:\.[^\s@.]+)*@(?:[\p{L}\p{N}](?:[\p{L}\p{N}-]*[\p{L}\p{N}])?\.)+(?:\p{L}{2,}|xn--[a-z0-9-]+)$/iu;
    window.theme = Object.assign(window.theme || {}, { EMAIL });
    const isEmail = (el) => el instanceof HTMLInputElement && el.type === 'email' && !el.readOnly && !el.disabled;
    const check = (input) => {
      const value = input.value.trim();
      input.setCustomValidity(value && !EMAIL.test(value) ? (window.theme.strings?.emailInvalid || input.validationMessage || ' ') : '');
    };
    document.addEventListener('input', (e) => { if (isEmail(e.target)) check(e.target); }, true);
    document.addEventListener('invalid', (e) => {
      const input = e.target;
      if (!isEmail(input)) return;
      check(input);
      if (!input.value.trim() && window.theme.strings?.emailRequired) input.setCustomValidity(window.theme.strings.emailRequired);
    }, true);
    // Values filled in by the server (a form posted back with an error) are checked too.
    const sweep = () => document.querySelectorAll('input[type="email"]').forEach((input) => { if (isEmail(input) && input.value) check(input); });
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sweep, { once: true }); else sweep();
  })();

  /* ---- Search placeholders ---- */
  (() => {
    const fields = document.querySelectorAll('input[data-placeholders]');
    if (!fields.length || REDUCED) return;
    fields.forEach((field) => {
      const list = field.dataset.placeholders.split('|').map((s) => s.trim()).filter(Boolean);
      if (!list.length) return;
      let i = 0, text = field.placeholder, deleting = false;
      let shown = false;
      new IntersectionObserver(([e]) => { shown = e.isIntersecting; }).observe(field);
      const step = () => {
        let wait = 70;
        if (field.value || !shown) {
          wait = 800;
        } else if (!deleting && text === list[i]) {
          if (list.length === 1) return;
          deleting = true; wait = 2200;
        } else if (deleting && !text) {
          deleting = false; i = (i + 1) % list.length; wait = 350;
        } else {
          text = deleting ? text.slice(0, -1) : list[i].slice(0, text.length + 1);
          field.placeholder = text;
          wait = deleting ? 35 : 75;
        }
        setTimeout(step, wait);
      };
      step();
    });
  })();

  /* ---- <ft-quickview> ---- */
  class FtQuickView extends HTMLElement {
    connectedCallback() {
      this.modal = this.querySelector('ft-modal');
      this.content = this.querySelector('[data-quickview-content]');
      this.cache = new Map();
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-quickview]');
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();
        this.show(btn.getAttribute('data-quickview'), btn);
      }, true);
      document.addEventListener('quickview:open', (e) => this.show(e.detail.url, e.detail.trigger));
      document.addEventListener('cart:added', () => {
        if (this.modal.hasAttribute('open')) this.modal.close();
      });
      this.modal.addEventListener('ft:closed', () => { this.content.innerHTML = ''; });
    }

    static templateUrl(url) {
      try {
        const u = new URL(url.replace(/\?view=quickview$/, ''), location.origin);
        u.searchParams.set('view', 'quickview');
        return u.pathname + u.search;
      } catch (e) { return url; }
    }

    async load(url) {
      url = FtQuickView.templateUrl(url);
      if (this.cache.has(url)) return this.cache.get(url);
      const res = await fetch(url);
      if (!res.ok) throw new Error(res.status);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const section = doc.querySelector('product-info')?.closest('.shopify-section');
      if (!section) throw new Error('no product section');
      this.cache.set(url, section.innerHTML);
      return section.innerHTML;
    }

    runScripts(root) {
      const known = new Set([...document.querySelectorAll('script[src]')].filter((el) => !root.contains(el)).map((el) => el.src));
      root.querySelectorAll('script').forEach((old) => {
        const type = old.getAttribute('type');
        if (type && type !== 'text/javascript' && type !== 'module') return;
        if (old.src && known.has(old.src)) { old.remove(); return; }
        const script = document.createElement('script');
        [...old.attributes].forEach((a) => script.setAttribute(a.name, a.value));
        if (old.src) { script.async = false; known.add(old.src); document.head.appendChild(script); old.remove(); }
        else { script.textContent = old.textContent; old.replaceWith(script); }
      });
    }

    async show(url, trigger) {
      if (this.pending === url) return;
      this.pending = url;
      const button = trigger?.matches?.('[data-quickview]') ? trigger : null;
      const isOpen = this.modal.hasAttribute('open');
      if (button && !isOpen) button.setAttribute('aria-busy', 'true');
      else this.content.setAttribute('aria-busy', 'true');

      const minimum = new Promise((r) => setTimeout(r, button && !isOpen ? (REDUCED ? 200 : 600) : 0));
      let html;
      try {
        [html] = await Promise.all([this.load(url), minimum]);
      } catch (err) {
        console.warn('[ft] quick view', err);
        button?.removeAttribute('aria-busy');
        this.pending = null;
        location.href = url.split('?')[0];
        return;
      }

      if (button && !isOpen) {
        button.removeAttribute('aria-busy');
        button.classList.add('is-loaded');
        await new Promise((r) => setTimeout(r, REDUCED ? 150 : 450));
        button.classList.remove('is-loaded');
      }
      this.pending = null;

      this.modal.open(trigger);
      this.content.innerHTML = html;
      this.runScripts(this.content);
      window.Shopify?.PaymentButton?.init?.();
      this.content.scrollTop = 0;
      this.content.setAttribute('aria-busy', 'false');
      if (isOpen && M && !REDUCED) M.animate(this.content.children, { opacity: [0, 1], transform: ['translateY(12px)', 'translateY(0)'] }, { duration: 0.4, delay: M.stagger(0.06), easing: EASE });
    }
  }
  safeDefine('ft-quickview', FtQuickView);

  /* ---- Product card hover picture, fetched on first hover ---- */
  // component.css keeps .product-card__hover out of the page until the card is armed, so a grid doesn't download
  // every card's second image on load. A mouse reaching the card arms it; the picture loads and fades in as before.
  document.addEventListener('pointerover', (e) => {
    if (e.pointerType !== 'mouse') return;
    const card = e.target.closest?.('.product-card');
    if (card && !card.classList.contains('hover-armed')) card.classList.add('hover-armed');
  }, { passive: true });

  /* ---- Button hover label ---- */
  (() => {
    const CLONE_EFFECTS = new Set(['reveal']);
    if (!CLONE_EFFECTS.has(document.body.dataset.buttonHover)) return;
    const SEL = '.button:not(.button--link), .btn:not(.button--link)';

    const sync = (btn) => {
      const text = btn.querySelector(':scope > .button__text');
      const host = btn.querySelector(':scope > .button__hover');
      if (!text || !host) return;
      const copy = text.cloneNode(true);
      copy.querySelectorAll('*').forEach((el) => {
        el.removeAttribute('id'); el.removeAttribute('name');
        Object.keys(el.dataset).forEach((k) => delete el.dataset[k]);
      });
      host.replaceChildren(...copy.childNodes);
    };

    const mirror = new MutationObserver((records) => {
      const seen = new Set();
      records.forEach((r) => {
        const node = r.target.nodeType === 1 ? r.target : r.target.parentElement;
        const btn = node?.closest?.(SEL);
        if (btn && !seen.has(btn)) { seen.add(btn); sync(btn); }
      });
    });

    const decorate = (btn) => {
      if (btn.querySelector(':scope > .button__hover')) return;
      const text = document.createElement('span');
      text.className = 'button__text';
      text.append(...btn.childNodes);
      const host = document.createElement('span');
      host.className = 'button__hover';
      host.setAttribute('aria-hidden', 'true');
      btn.append(text, host);
      sync(btn);
      mirror.observe(text, { childList: true, characterData: true, subtree: true });
    };

    // Buttons are decorated in small slices between tasks, so a page full of product cards never blocks input.
    const queue = new Set();
    let draining = false;
    const drain = async () => {
      draining = true;
      let start = performance.now();
      for (const btn of queue) {
        queue.delete(btn);
        if (btn.isConnected) decorate(btn);
        if (performance.now() - start > 8) { await nextTask(); start = performance.now(); }
      }
      draining = false;
      if (queue.size) drain();
    };
    const enqueue = (btn) => { queue.add(btn); if (!draining) drain(); };

    const sweep = (root = document) => {
      if (root.nodeType !== 1 && root !== document) return;
      if (root.matches?.(SEL)) enqueue(root);
      root.querySelectorAll?.(SEL).forEach(enqueue);
      const owner = root.closest?.(SEL);
      if (owner) enqueue(owner);
    };
    if (window.matchMedia('(hover: none)').matches && !DESIGN) {
      // Touch screens have no hover: rather than rebuilding every button at load, a button gets its hover label the
      // moment it is touched or focused, before :hover / :focus-visible apply, so the effect still plays.
      const onDemand = (e) => { const btn = e.target.closest?.(SEL); if (btn) decorate(btn); };
      document.addEventListener('pointerdown', onDemand, { capture: true, passive: true });
      document.addEventListener('focusin', onDemand, true);
    } else nextTask().then(() => {
      sweep();
      new MutationObserver((ms) => ms.forEach((m) => m.addedNodes.forEach((n) => n.nodeType === 1 && sweep(n)))).observe(document.body, { childList: true, subtree: true });
    });
    if (DESIGN) document.addEventListener('shopify:section:load', (e) => sweep(e.target));
  })();

  /* ---- Editor ---- */
  if (DESIGN) (() => {
    const OPEN = '<span class="text-highlight">';
    const marked = (block) => {
      block.querySelectorAll('em').forEach((em) => {
        const span = document.createElement('span');
        span.className = 'text-highlight';
        span.append(...em.childNodes);
        em.replaceWith(span);
      });
      const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT);
      const hits = [];
      while (walker.nextNode()) if (/[[\]]/.test(walker.currentNode.nodeValue)) hits.push(walker.currentNode);
      if (!hits.length) return;
      const range = document.createRange();
      range.setStartBefore(hits[0]); range.setEndAfter(hits[hits.length - 1]);
      let host = range.commonAncestorContainer;
      if (host.nodeType !== 1) host = host.parentElement;
      host.innerHTML = host.innerHTML.replace(/\[/g, OPEN).replace(/\]/g, '</span>');
    };
    const pending = new Set();
    let queued = false;
    new MutationObserver((ms) => {
      ms.forEach((m) => {
        const el = m.target.nodeType === 1 ? m.target : m.target.parentElement;
        const block = el?.closest('.text-block');
        if (block) pending.add(block);
      });
      if (queued || !pending.size) return;
      queued = true;
      queueMicrotask(() => { queued = false; pending.forEach(marked); pending.clear(); });
    }).observe(document.body, { childList: true, characterData: true, subtree: true });
    document.querySelectorAll('.text-block').forEach(marked);
  })();

})();
